import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { QueryParamConfig, UrlUpdateType } from 'use-query-params';
import { ArrayParam, BooleanParam, NumberParam, StringParam, useQueryParam } from 'use-query-params';
import { normalizeFlowsString } from 'utils';
import { APP_LIST, APP_PLATFORMS, MARKETING_PROCESS, MarketingProcessCode, Platform, type AppCode } from '../constants';
import { isFacebookAdTypeCode, type FacebookAdTypeCode } from 'features/facebook/constants';
import {
  DEFAULT_HYPOTHESIS_CREATIVES_VIEW_STATE,
  PartnershipCreationMode,
  PartnershipIdentityToShow,
  type LaunchStatus,
  type UploadedFilter,
} from 'features/facebook/types';
import type { RowStatus } from 'features/facebook/api/getCampaigns';

export const DEFAULT_MARKETING_PROCESS = MarketingProcessCode.SCALING;
export const DEFAULT_VISUAL_TOUCH_UPS = true;
export const DEFAULT_PLATFORM = Platform.WEB;
export const DEFAULT_CALL_TO_ACTION = 'LEARN_MORE';
export const DEFAULT_HOTJAR = false;

const CREATIVES_ID_SEPARATOR = '~';
const PACK_FIELD_SEPARATOR = '~';
const PACK_CREATIVE_ID_SEPARATOR = ',';
const LEGACY_PACK_KEY_SEPARATOR = ':';
const LEGACY_VISUAL_TOUCH_UPS_FLAG = /\*([01])$/;

export interface PackCreativesGroup {
  tag?: string;
  creatives: readonly (string | undefined)[];
  visualTouchUps?: boolean;
}

export interface DecodedPackCreativesGroup {
  index: number;
  tag: string;
  creatives: string[];
  visualTouchUps?: boolean;
}

const PACK_INDEX_KEY_PREFIX = '#';

const MAX_PLAUSIBLE_PACK_INDEX = 50;

/**
 * `encodeURIComponent` / `decodeURIComponent` throw `URIError: URI malformed` on
 * lone surrogates (encode) or incomplete `%` sequences (decode). Hypothesis pack
 * tags and free-text filters are user/API-controlled and can contain `%` (e.g.
 * "100%"), so every URL codec must tolerate that without crashing the form.
 *
 * Pack tags are also embedded inside the `creatives` value before the router's
 * outer encode, so after `query-string` / `URLSearchParams` decode once a tag
 * like "100%" arrives as the bare `100%` — a second `decodeURIComponent` must
 * not throw.
 */
export const safeEncodeURIComponent = (value: string): string => {
  try {
    return encodeURIComponent(value);
  } catch {
    return encodeURIComponent(value.replace(/[\uD800-\uDFFF]/g, '\uFFFD'));
  }
};

export const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/** Pack-tag keys: encodeURIComponent leaves `~` unescaped, but `~` is our field separator. */
const encodePackTagKey = (tag: string): string =>
  safeEncodeURIComponent(tag).replace(/~/g, '%7E');

const joinCommaSplit = (
  raw: string | (string | null)[] | null | undefined,
): string | undefined => {
  if (raw == null) return undefined;
  if (Array.isArray(raw)) {
    return raw.filter((part): part is string => typeof part === 'string').join(',') || undefined;
  }
  return raw || undefined;
};

const CommaJoinStringParam: QueryParamConfig<string | undefined> = {
  encode: (value) => value,
  decode: (value) => joinCommaSplit(value) ?? undefined,
};

const isVisualTouchUpsFlag = (value: string): value is '0' | '1' =>
  value === '0' || value === '1';

const isNewPackFormat = (entry: string): boolean => {
  const tildeIndex = entry.indexOf(PACK_FIELD_SEPARATOR);
  const colonIndex = entry.indexOf(LEGACY_PACK_KEY_SEPARATOR);
  return tildeIndex > 0 && (colonIndex === -1 || tildeIndex < colonIndex);
};

const packKeySeparatorIndex = (entry: string): number =>
  isNewPackFormat(entry)
    ? entry.indexOf(PACK_FIELD_SEPARATOR)
    : entry.indexOf(LEGACY_PACK_KEY_SEPARATOR);

export const encodeCreativesByPack = (
  packs: readonly PackCreativesGroup[],
): string | undefined => {
  const entries = packs
    .map((pack, index) => {
      const ids = (pack.creatives ?? []).filter(
        (id): id is string => typeof id === 'string',
      );
      const tag = pack.tag?.trim();
      const key = tag ? encodePackTagKey(tag) : `${PACK_INDEX_KEY_PREFIX}${index}`;
      return { key, ids, visualTouchUps: pack.visualTouchUps };
    })
    .filter(
      ({ ids, visualTouchUps }) => ids.length > 0 || visualTouchUps === false,
    )
    .map(({ key, ids, visualTouchUps }) => {
      const idsPart = ids.join(PACK_CREATIVE_ID_SEPARATOR);
      if (visualTouchUps === undefined) {
        return `${key}${PACK_FIELD_SEPARATOR}${idsPart}`;
      }
      return `${key}${PACK_FIELD_SEPARATOR}${idsPart}${PACK_FIELD_SEPARATOR}${visualTouchUps ? '1' : '0'}`;
    });

  return entries.length ? entries.join(';') : undefined;
};

const parsePackEntryIds = (
  rawIds: string,
  usesNewFormat: boolean,
): { creatives: string[]; visualTouchUps?: boolean } | null => {
  if (usesNewFormat) {
    const lastTilde = rawIds.lastIndexOf(PACK_FIELD_SEPARATOR);
    let idsPart = rawIds;
    let visualTouchUps: boolean | undefined;
    if (lastTilde !== -1) {
      const maybeFlag = rawIds.slice(lastTilde + 1);
      if (isVisualTouchUpsFlag(maybeFlag)) {
        visualTouchUps = maybeFlag === '1';
        idsPart = rawIds.slice(0, lastTilde);
      }
    }
    if (idsPart.includes(PACK_FIELD_SEPARATOR)) return null;

    const creatives = idsPart
      ? idsPart.split(PACK_CREATIVE_ID_SEPARATOR).filter(Boolean)
      : [];
    if (!creatives.length && visualTouchUps !== false) return null;
    return visualTouchUps === undefined ? { creatives } : { creatives, visualTouchUps };
  }

  if (!rawIds) return null;

  const flagMatch = LEGACY_VISUAL_TOUCH_UPS_FLAG.exec(rawIds);
  const visualTouchUps =
    flagMatch?.[1] === '1' ? true : flagMatch?.[1] === '0' ? false : undefined;
  const idsPart = flagMatch ? rawIds.slice(0, flagMatch.index) : rawIds;
  const creatives = idsPart
    ? idsPart.split(CREATIVES_ID_SEPARATOR).filter(Boolean)
    : [];
  if (!creatives.length && visualTouchUps !== false) return null;
  return visualTouchUps === undefined ? { creatives } : { creatives, visualTouchUps };
};

export const decodeCreativesByPack = (
  raw: string | (string | null)[] | null | undefined,
): DecodedPackCreativesGroup[] => {
  const joined = joinCommaSplit(raw);
  if (!joined) return [];

  const groups: DecodedPackCreativesGroup[] = [];

  let cursor = 0;

  for (const entry of joined.split(';')) {
    const separatorIndex = packKeySeparatorIndex(entry);
    if (separatorIndex <= 0) continue;

    const key = entry.slice(0, separatorIndex);
    const parsed = parsePackEntryIds(entry.slice(separatorIndex + 1), isNewPackFormat(entry));
    if (!parsed) continue;

    const { creatives, visualTouchUps } = parsed;

    const isPrefixedKey = key.startsWith(PACK_INDEX_KEY_PREFIX);
    const digits = isPrefixedKey ? key.slice(PACK_INDEX_KEY_PREFIX.length) : key;
    const isIndexKey = /^\d+$/.test(digits) && Number(digits) < MAX_PLAUSIBLE_PACK_INDEX;
    const index = isIndexKey ? Number(digits) : cursor;
    const tag = isIndexKey ? '' : safeDecodeURIComponent(digits);

    groups.push(
      visualTouchUps === undefined
        ? { index, tag, creatives }
        : { index, tag, creatives, visualTouchUps },
    );
    cursor = index + 1;
  }

  return groups;
};

export const CREATIVE_ID_PATTERN = /^(image|video):(\d+)$/;

export const isCreativeId = (value: string): boolean => CREATIVE_ID_PATTERN.test(value);

export const parseCreativeIdDigest = (value: string): string | undefined => {
  const match = CREATIVE_ID_PATTERN.exec(value);
  return match?.[2];
};

export const parseCreativeIdDigests = (
  creativeIds: readonly string[],
): string[] => [
  ...new Set(
    creativeIds.flatMap((id) => {
      const digest = parseCreativeIdDigest(id);
      return digest ? [digest] : [];
    }),
  ),
];

export const detectCreativesParamFormat = (
  raw: string | (string | null)[] | null | undefined,
): 'pack' | 'flat' | 'empty' => {
  const joined = joinCommaSplit(raw);
  if (!joined) return 'empty';

  const isPackGroup = (entry: string): boolean => {
    const separatorIndex = packKeySeparatorIndex(entry);
    if (separatorIndex <= 0) return false;

    if (isNewPackFormat(entry)) {
      const parsed = parsePackEntryIds(entry.slice(separatorIndex + 1), true);
      if (!parsed) return false;
    }

    const key = entry.slice(0, separatorIndex);
    if (key.startsWith(PACK_INDEX_KEY_PREFIX)) {
      const digits = key.slice(PACK_INDEX_KEY_PREFIX.length);
      return /^\d+$/.test(digits) && Number(digits) < MAX_PLAUSIBLE_PACK_INDEX;
    }
    if (/^\d+$/.test(key) && Number(key) < MAX_PLAUSIBLE_PACK_INDEX) return true;
    return key !== 'image' && key !== 'video';
  };

  return joined.split(';').some(isPackGroup) ? 'pack' : 'flat';
};

export interface CampaignSelectionEntry {
  id: string;
  budget?: string;
  status?: RowStatus;
}

const isRowStatus = (value: string | undefined): value is RowStatus =>
  value === 'ACTIVE' || value === 'PAUSED';

const CAMPAIGN_FIELD_SEPARATOR = '~';

export const encodeSelectedCampaigns = (
  entries: readonly CampaignSelectionEntry[],
): string | undefined => {
  const parts = entries
    .filter(({ id }) => !!id)
    .map(({ id, budget, status }) =>
      [id, budget ?? '', status ?? ''].join(CAMPAIGN_FIELD_SEPARATOR),
    );

  return parts.length ? parts.join(';') : undefined;
};

export const decodeSelectedCampaigns = (
  raw: string | null | undefined,
): CampaignSelectionEntry[] => {
  if (!raw) return [];

  return raw
    .split(';')
    .filter(Boolean)
    .map((entry) => {
      const separator = entry.includes(CAMPAIGN_FIELD_SEPARATOR)
        ? CAMPAIGN_FIELD_SEPARATOR
        : ':';
      const [id, budget, status] = entry.split(separator);
      return {
        id: id ?? '',
        budget: budget || undefined,
        status: isRowStatus(status) ? status : undefined,
      };
    })
    .filter(({ id }) => !!id);
};

export interface AdSetSelectionEntry {
  id: string;
  budget?: string;
  status?: RowStatus;
  startTime?: string;
  duplicatesCount?: string;
  /** Comma-separated flow names (`"flowA,flowB"`) assigned on the ad set. */
  flow?: string;
}

const AD_SET_FIELD_SEPARATOR = '~';

const decodeAdSetFlowField = (raw: string | undefined): string | undefined => {
  if (!raw) return undefined;
  return normalizeFlowsString(safeDecodeURIComponent(raw));
};

const parseAdSetEntry = (entry: string): AdSetSelectionEntry => {
  const [id, budget, status, startTime, duplicatesCount, rawFlow] = entry.split(
    AD_SET_FIELD_SEPARATOR,
  );

  return {
    id: id ?? '',
    budget: budget || undefined,
    status: isRowStatus(status) ? status : undefined,
    startTime: startTime || undefined,
    duplicatesCount: duplicatesCount || undefined,
    flow: decodeAdSetFlowField(rawFlow),
  };
};

export const encodeSelectedAdSets = (
  entries: readonly AdSetSelectionEntry[],
): string | undefined => {
  const parts = entries
    .filter(({ id }) => !!id)
    .map(({ id, budget, status, startTime, duplicatesCount, flow }) => {
      const fields = [
        id,
        budget ?? '',
        status ?? '',
        startTime ?? '',
        duplicatesCount ?? '',
      ];
      const normalizedFlow = normalizeFlowsString(flow);
      if (normalizedFlow) fields.push(normalizedFlow);
      return fields.join(AD_SET_FIELD_SEPARATOR);
    });

  return parts.length ? parts.join(';') : undefined;
};

export const decodeSelectedAdSets = (
  raw: string | (string | null)[] | null | undefined,
): AdSetSelectionEntry[] => {
  const joined = joinCommaSplit(raw);
  if (!joined) return [];

  return joined
    .split(';')
    .filter(Boolean)
    .map(parseAdSetEntry)
    .filter(({ id }) => !!id);
};

export const encodeFlatCreatives = (ids: readonly string[]): string | undefined =>
  ids.length ? ids.join(CREATIVES_ID_SEPARATOR) : undefined;

export const decodeFlatCreatives = (raw: string | null | undefined): string[] =>
  raw ? raw.split(CREATIVES_ID_SEPARATOR).filter(Boolean) : [];

export interface AdSettingsFlowSelection {
  page_id: string | number;
  text_id: string | number;
}

export type AdSettingsByIndex = Record<string, AdSettingsFlowSelection>[];

const AD_SETTINGS_INDEX_PREFIX = '#';
const AD_SETTINGS_FLOW_SEPARATOR = '|';
const AD_SETTINGS_FLOW_KV_SEPARATOR = '=';
const AD_SETTINGS_ID_SEPARATOR = '~';

export const encodeAdSettingsByIndex = (
  slots: readonly Record<string, AdSettingsFlowSelection | undefined>[],
): string | undefined => {
  const groups = slots
    .map((slot, index) => {
      const flows = Object.entries(slot ?? {})
        .filter(([, value]) => value && (value.page_id || value.text_id))
        .map(([flowName, value]) => {
          const pageId = String(value?.page_id ?? '');
          const textId = String(value?.text_id ?? '');
          return `${safeEncodeURIComponent(flowName)}${AD_SETTINGS_FLOW_KV_SEPARATOR}${pageId}${AD_SETTINGS_ID_SEPARATOR}${textId}`;
        });

      if (!flows.length) return undefined;
      return `${AD_SETTINGS_INDEX_PREFIX}${index}:${flows.join(AD_SETTINGS_FLOW_SEPARATOR)}`;
    })
    .filter((group): group is string => !!group);

  return groups.length ? groups.join(';') : undefined;
};

export const decodeAdSettingsByIndex = (
  raw: string | null | undefined,
): AdSettingsByIndex => {
  if (!raw) return [];

  const slots: AdSettingsByIndex = [];

  for (const entry of raw.split(';')) {
    if (!entry) continue;

    const separatorIndex = entry.indexOf(':');
    if (separatorIndex <= 0) continue;

    const key = entry.slice(0, separatorIndex);
    const flowEntries = entry.slice(separatorIndex + 1);
    if (!key.startsWith(AD_SETTINGS_INDEX_PREFIX) || !flowEntries) continue;

    const digits = key.slice(AD_SETTINGS_INDEX_PREFIX.length);
    if (!/^\d+$/.test(digits)) continue;

    const index = Number(digits);
    const slot: Record<string, AdSettingsFlowSelection> = {};

    for (const flowEntry of flowEntries.split(AD_SETTINGS_FLOW_SEPARATOR)) {
      const kvIndex = flowEntry.indexOf(AD_SETTINGS_FLOW_KV_SEPARATOR);
      if (kvIndex <= 0) continue;

      const flowName = safeDecodeURIComponent(flowEntry.slice(0, kvIndex));
      const [pageId = '', textId = ''] = flowEntry
        .slice(kvIndex + 1)
        .split(AD_SETTINGS_ID_SEPARATOR);

      if (!flowName) continue;
      slot[flowName] = {
        page_id: /^\d+$/.test(pageId) ? Number(pageId) : pageId,
        text_id: /^\d+$/.test(textId) ? Number(textId) : textId,
      };
    }

    if (!Object.keys(slot).length) continue;

    while (slots.length <= index) {
      slots.push({});
    }
    slots[index] = slot;
  }

  return slots;
};

const isPartnershipIdentityToShow = (
  value: string | null | undefined,
): value is PartnershipIdentityToShow =>
  value === PartnershipIdentityToShow.BOTH
  || value === PartnershipIdentityToShow.FIRST_ONLY
  || value === PartnershipIdentityToShow.DYNAMIC;

const isPartnershipCreationMode = (
  value: string | null | undefined,
): value is PartnershipCreationMode =>
  value === PartnershipCreationMode.PARTNERSHIP_ONLY
  || value === PartnershipCreationMode.BOTH;

// Free text (search box, customer name) can contain a raw comma, which the
// router's `arrayFormat: 'comma'` would otherwise split into an array before
// StringParam ever sees it (same issue CREATIVES_ID_SEPARATOR works around
// above), so it's round-tripped through encodeURIComponent.
export const encodeFreeText = (value: string): string | undefined =>
  value ? safeEncodeURIComponent(value) : undefined;

export const decodeFreeText = (raw: string | null | undefined): string =>
  raw ? safeDecodeURIComponent(raw) : '';

export const encodeFreeTextOrNull = (value: string | null): string | undefined =>
  value ? safeEncodeURIComponent(value) : undefined;

export const decodeFreeTextOrNull = (raw: string | null | undefined): string | null =>
  raw ? safeDecodeURIComponent(raw) : null;

const FILTER_LIST_SEPARATOR = ';';

export const encodeFreeTextList = (values: readonly string[]): string | undefined =>
  values.length
    ? values.map(safeEncodeURIComponent).join(FILTER_LIST_SEPARATOR)
    : undefined;

export const decodeFreeTextList = (raw: string | null | undefined): string[] =>
  raw
    ? raw.split(FILTER_LIST_SEPARATOR).filter(Boolean).map(safeDecodeURIComponent)
    : [];

const UPLOADED_FILTER_VALUES: readonly UploadedFilter[] = [
  'last_week',
  'last_2_weeks',
  '1_month',
  'all',
];

const isUploadedFilter = (
  value: string | null | undefined,
): value is UploadedFilter =>
  !!value && (UPLOADED_FILTER_VALUES as readonly string[]).includes(value);

const LAUNCH_STATUS_VALUES: readonly LaunchStatus[] = ['launched', 'not_launched'];

const isLaunchStatus = (value: string | null | undefined): value is LaunchStatus =>
  !!value && (LAUNCH_STATUS_VALUES as readonly string[]).includes(value);

const MARKETING_PROCESS_CODES: readonly string[] = MARKETING_PROCESS.map(
  ({ code }) => code,
);

const isMarketingProcessCode = (
  value: string | null | undefined,
): value is MarketingProcessCode =>
  !!value && MARKETING_PROCESS_CODES.includes(value);

const APP_CODES: readonly string[] = APP_LIST.map(({ code }) => code);

const isAppCode = (value: string | null | undefined): value is AppCode =>
  !!value && APP_CODES.includes(value);

const PLATFORM_CODES: readonly string[] = APP_PLATFORMS.map(({ code }) => code);

const isPlatformCode = (value: string | null | undefined): value is Platform =>
  !!value && PLATFORM_CODES.includes(value);

const arraysEqual = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const APP_QUERY_KEY = 'app';

export const hasParamsBesidesApp = (search: string): boolean => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

  for (const key of params.keys()) {
    if (key !== APP_QUERY_KEY) return true;
  }

  return false;
};

/** Query string (with a leading `?`, or empty) a restarted form starts from. */
export const buildStartSearch = (
  app: AppCode | undefined,
  marketingProcess: MarketingProcessCode,
): string => {
  const params = new URLSearchParams();
  if (app) {
    params.set('app', app);
  }
  if (marketingProcess !== DEFAULT_MARKETING_PROCESS) {
    params.set('marketingProcess', marketingProcess);
  }

  const search = params.toString();

  return search ? `?${search}` : '';
};

const useLocationParams = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const hasConfiguredParams = useMemo(() => hasParamsBesidesApp(search), [search]);

  const [rawMarketingProcess, setRawMarketingProcess] = useQueryParam(
    'marketingProcess',
    StringParam,
  );

  const marketingProcess = isMarketingProcessCode(rawMarketingProcess)
    ? rawMarketingProcess
    : DEFAULT_MARKETING_PROCESS;

  const setMarketingProcess = useCallback(
    (nextValue: MarketingProcessCode) => {
      if (nextValue === marketingProcess) return;
      setRawMarketingProcess(nextValue, 'pushIn');
    },
    [marketingProcess, setRawMarketingProcess],
  );

  const [rawApp, setRawApp] = useQueryParam('app', StringParam);

  const app = isAppCode(rawApp) ? rawApp : undefined;

  const setApp = useCallback(
    (nextValue: AppCode) => {
      if (nextValue === app) return;
      setRawApp(nextValue, 'pushIn');
    },
    [app, setRawApp],
  );

  // `push` (not `pushIn`) replaces the entire query with just `app=`, so
  // campaigns/creatives/ad sets and the rest do not leak into the new app.
  const resetLocationParamsForApp = useCallback(
    (nextValue: AppCode) => {
      setRawApp(nextValue, 'push');
    },
    [setRawApp],
  );

  const startSearch = useMemo(
    () => buildStartSearch(app, marketingProcess),
    [app, marketingProcess],
  );

  // Drops every deep-link param (creatives, campaigns, ad sets, step, …) and
  // keeps only `app` plus a non-default marketing process, so the form opens
  // on step 0 as a fresh session.
  const resetLocationParamsToStart = useCallback(() => {
    const currentSearch = search.startsWith('?') ? search.slice(1) : search;
    if (currentSearch === startSearch.slice(1)) return;

    navigate({ pathname, search: startSearch }, { replace: true });
  }, [navigate, pathname, search, startSearch]);

  // Same clean URL as `resetLocationParamsToStart`, but via a full page load:
  // a restart must also drop in-memory form state, React Query caches and any
  // in-flight job polling, which a client-side navigation would keep alive.
  const reloadLocationParamsToStart = useCallback(() => {
    window.location.replace(`${pathname}${startSearch}`);
  }, [pathname, startSearch]);

  const [rawAdType, setRawAdType] = useQueryParam('ad_type', StringParam);

  const adType = isFacebookAdTypeCode(rawAdType) ? rawAdType : undefined;

  const setAdType = useCallback(
    (nextValue: FacebookAdTypeCode | undefined) => {
      if (nextValue === adType) return;
      setRawAdType(nextValue, 'pushIn');
    },
    [adType, setRawAdType],
  );

  const [rawPlatform, setRawPlatform] = useQueryParam('platform', StringParam);

  const platform = isPlatformCode(rawPlatform) ? rawPlatform : DEFAULT_PLATFORM;

  const setPlatform = useCallback(
    (nextValue: Platform) => {
      if (nextValue === platform) return;
      setRawPlatform(nextValue, 'pushIn');
    },
    [platform, setRawPlatform],
  );

  const [rawAdAccounts, setRawAdAccounts] = useQueryParam('ad_accounts', ArrayParam);

  const adAccounts = useMemo(
    () => (rawAdAccounts ?? []).filter((id): id is string => !!id),
    [rawAdAccounts],
  );

  const setAdAccounts = useCallback(
    (nextValue: readonly string[]) => {
      if (arraysEqual(nextValue, adAccounts)) return;
      setRawAdAccounts(nextValue.length ? [...nextValue] : undefined, 'replaceIn');
    },
    [adAccounts, setRawAdAccounts],
  );

  const [rawAdAccount, setRawAdAccount] = useQueryParam('ad_account', StringParam);
  const adAccount = rawAdAccount || undefined;
  const setAdAccount = useCallback(
    (nextValue: string | undefined) => {
      const next = nextValue || undefined;
      if (next === adAccount) return;
      setRawAdAccount(next, 'replaceIn');
    },
    [adAccount, setRawAdAccount],
  );

  const [rawVisualTouchUps, setRawVisualTouchUps] = useQueryParam(
    'visual_touch_ups',
    BooleanParam,
  );

  const visualTouchUps = rawVisualTouchUps ?? DEFAULT_VISUAL_TOUCH_UPS;

  const setVisualTouchUps = useCallback(
    (nextValue: boolean) => {
      if (nextValue === visualTouchUps) return;
      setRawVisualTouchUps(nextValue, 'pushIn');
    },
    [visualTouchUps, setRawVisualTouchUps],
  );

  const [rawCallToAction, setRawCallToAction] = useQueryParam(
    'call_to_action',
    StringParam,
  );
  const callToAction = rawCallToAction || undefined;
  const setCallToAction = useCallback(
    (nextValue: string | undefined) => {
      const next = nextValue || undefined;
      if (next === callToAction) return;
      setRawCallToAction(next, 'replaceIn');
    },
    [callToAction, setRawCallToAction],
  );

  const [rawConversionDomain, setRawConversionDomain] = useQueryParam(
    'conversion_domain',
    StringParam,
  );
  const conversionDomain = rawConversionDomain || undefined;
  const setConversionDomain = useCallback(
    (nextValue: string | undefined) => {
      const next = nextValue || undefined;
      if (next === conversionDomain) return;
      setRawConversionDomain(next, 'replaceIn');
    },
    [conversionDomain, setRawConversionDomain],
  );

  const [rawHotjar, setRawHotjar] = useQueryParam('hotjar', BooleanParam);
  const hotjar = rawHotjar ?? DEFAULT_HOTJAR;
  const setHotjar = useCallback(
    (nextValue: boolean) => {
      if (nextValue === hotjar) return;
      setRawHotjar(nextValue ? true : undefined, 'replaceIn');
    },
    [hotjar, setRawHotjar],
  );

  const [rawPartnershipAdsEnabled, setRawPartnershipAdsEnabled] = useQueryParam(
    'partnership_ads',
    BooleanParam,
  );
  const partnershipAdsEnabled = rawPartnershipAdsEnabled ?? false;
  const setPartnershipAdsEnabled = useCallback(
    (nextValue: boolean) => {
      if (nextValue === partnershipAdsEnabled) return;
      setRawPartnershipAdsEnabled(nextValue ? true : undefined, 'replaceIn');
    },
    [partnershipAdsEnabled, setRawPartnershipAdsEnabled],
  );

  const [rawIdentityToShow, setRawIdentityToShow] = useQueryParam(
    'identity_to_show',
    StringParam,
  );
  const partnershipIdentityToShow = isPartnershipIdentityToShow(rawIdentityToShow)
    ? rawIdentityToShow
    : undefined;
  const setPartnershipIdentityToShow = useCallback(
    (nextValue: PartnershipIdentityToShow | undefined) => {
      if (nextValue === partnershipIdentityToShow) return;
      setRawIdentityToShow(nextValue, 'replaceIn');
    },
    [partnershipIdentityToShow, setRawIdentityToShow],
  );

  const [rawIdentityFirst, setRawIdentityFirst] = useQueryParam(
    'identity_first',
    BooleanParam,
  );
  const partnershipIsPartnerIdentityFirst =
    typeof rawIdentityFirst === 'boolean' ? rawIdentityFirst : undefined;
  const setPartnershipIsPartnerIdentityFirst = useCallback(
    (nextValue: boolean | undefined) => {
      if (nextValue === partnershipIsPartnerIdentityFirst) return;
      setRawIdentityFirst(nextValue, 'replaceIn');
    },
    [partnershipIsPartnerIdentityFirst, setRawIdentityFirst],
  );

  const [rawCreationMode, setRawCreationMode] = useQueryParam(
    'creation_mode',
    StringParam,
  );
  const partnershipCreationMode = isPartnershipCreationMode(rawCreationMode)
    ? rawCreationMode
    : undefined;
  const setPartnershipCreationMode = useCallback(
    (nextValue: PartnershipCreationMode | undefined) => {
      if (nextValue === partnershipCreationMode) return;
      setRawCreationMode(nextValue, 'replaceIn');
    },
    [partnershipCreationMode, setRawCreationMode],
  );

  const [rawPartners, setRawPartners] = useQueryParam('partners', ArrayParam);
  const partners = useMemo(
    () => (rawPartners ?? []).filter((id): id is string => !!id),
    [rawPartners],
  );
  const setPartners = useCallback(
    (nextValue: readonly string[]) => {
      if (arraysEqual(nextValue, partners)) return;
      setRawPartners(nextValue.length ? [...nextValue] : undefined, 'replaceIn');
    },
    [partners, setRawPartners],
  );

  const [rawAdSettingsPack, setRawAdSettingsPack] = useQueryParam(
    'ad_settings_pack',
    StringParam,
  );
  const packAdSettings = useMemo(
    () => decodeAdSettingsByIndex(rawAdSettingsPack),
    [rawAdSettingsPack],
  );
  const setPackAdSettings = useCallback(
    (slots: readonly Record<string, AdSettingsFlowSelection | undefined>[]) => {
      const next = encodeAdSettingsByIndex(slots);
      if (next === rawAdSettingsPack) return;
      setRawAdSettingsPack(next, 'replaceIn');
    },
    [rawAdSettingsPack, setRawAdSettingsPack],
  );

  const [rawAdSettingsPartner, setRawAdSettingsPartner] = useQueryParam(
    'ad_settings_partner',
    StringParam,
  );
  const partnershipAdSettingsFields = useMemo(
    () => decodeAdSettingsByIndex(rawAdSettingsPartner),
    [rawAdSettingsPartner],
  );
  const setPartnershipAdSettingsFields = useCallback(
    (slots: readonly Record<string, AdSettingsFlowSelection | undefined>[]) => {
      const next = encodeAdSettingsByIndex(slots);
      if (next === rawAdSettingsPartner) return;
      setRawAdSettingsPartner(next, 'replaceIn');
    },
    [rawAdSettingsPartner, setRawAdSettingsPartner],
  );

  const [rawCreatives, setRawCreatives] = useQueryParam(
    'creatives',
    CommaJoinStringParam,
  );

  const creativesByPack = useMemo(
    () => decodeCreativesByPack(rawCreatives),
    [rawCreatives],
  );

  const flatCreatives = useMemo(
    () => decodeFlatCreatives(rawCreatives),
    [rawCreatives],
  );

  const creativesFormat = useMemo(
    () => detectCreativesParamFormat(rawCreatives),
    [rawCreatives],
  );

  const setCreativesByPack = useCallback(
    (packs: readonly PackCreativesGroup[]) => {
      const next = encodeCreativesByPack(packs);
      if (next === rawCreatives) return;
      setRawCreatives(next, 'replaceIn');
    },
    [rawCreatives, setRawCreatives],
  );

  const setFlatCreatives = useCallback(
    (ids: readonly string[]) => {
      const next = encodeFlatCreatives(ids);
      if (next === rawCreatives) return;
      setRawCreatives(next, 'replaceIn');
    },
    [rawCreatives, setRawCreatives],
  );

  const [rawSelectedCampaigns, setRawSelectedCampaigns] = useQueryParam(
    'campaigns',
    StringParam,
  );

  const selectedCampaigns = useMemo(
    () => decodeSelectedCampaigns(rawSelectedCampaigns),
    [rawSelectedCampaigns],
  );

  const setSelectedCampaigns = useCallback(
    (entries: readonly CampaignSelectionEntry[]) => {
      const next = encodeSelectedCampaigns(entries);
      if (next === rawSelectedCampaigns) return;
      setRawSelectedCampaigns(next, 'replaceIn');
    },
    [rawSelectedCampaigns, setRawSelectedCampaigns],
  );

  const [rawFilterCampaignsByName, setRawFilterCampaignsByName] = useQueryParam(
    'campaign_name',
    StringParam,
  );
  const filterCampaignsByName = useMemo(
    () => decodeFreeTextList(rawFilterCampaignsByName),
    [rawFilterCampaignsByName],
  );
  const setFilterCampaignsByName = useCallback(
    (nextValue: readonly string[]) => {
      const next = encodeFreeTextList(nextValue);
      if (next === rawFilterCampaignsByName) return;
      setRawFilterCampaignsByName(next, 'replaceIn');
    },
    [rawFilterCampaignsByName, setRawFilterCampaignsByName],
  );

  const [rawSelectedAdSets, setRawSelectedAdSets] = useQueryParam(
    'ad_sets',
    CommaJoinStringParam,
  );

  const selectedAdSets = useMemo(
    () => decodeSelectedAdSets(rawSelectedAdSets),
    [rawSelectedAdSets],
  );

  // Selection/budget/status/start-time/duplicate-count can change on nearly
  // every click or keystroke, so this replaces the current history entry
  // instead of pushing a new one.
  const setSelectedAdSets = useCallback(
    (entries: readonly AdSetSelectionEntry[]) => {
      const next = encodeSelectedAdSets(entries);
      if (next === rawSelectedAdSets) return;
      setRawSelectedAdSets(next, 'replaceIn');
    },
    [rawSelectedAdSets, setRawSelectedAdSets],
  );

  const [rawFilterAdSetsByName, setRawFilterAdSetsByName] = useQueryParam(
    'ad_set_name',
    StringParam,
  );
  const filterAdSetsByName = useMemo(
    () => decodeFreeTextList(rawFilterAdSetsByName),
    [rawFilterAdSetsByName],
  );
  const setFilterAdSetsByName = useCallback(
    (nextValue: readonly string[]) => {
      const next = encodeFreeTextList(nextValue);
      if (next === rawFilterAdSetsByName) return;
      setRawFilterAdSetsByName(next, 'replaceIn');
    },
    [rawFilterAdSetsByName, setRawFilterAdSetsByName],
  );

  const [rawFilterCreativesByName, setRawFilterCreativesByName] = useQueryParam(
    'creative_name',
    StringParam,
  );
  const filterCreativesByName = useMemo(
    () => decodeFreeTextList(rawFilterCreativesByName),
    [rawFilterCreativesByName],
  );
  const setFilterCreativesByName = useCallback(
    (nextValue: readonly string[]) => {
      const next = encodeFreeTextList(nextValue);
      if (next === rawFilterCreativesByName) return;
      setRawFilterCreativesByName(next, 'replaceIn');
    },
    [rawFilterCreativesByName, setRawFilterCreativesByName],
  );

  const [rawHypothesisSearch, setRawHypothesisSearch] = useQueryParam(
    'search',
    StringParam,
  );
  const hypothesisSearch = decodeFreeText(rawHypothesisSearch);
  const setHypothesisSearch = useCallback(
    (nextValue: string) => {
      const next = encodeFreeText(nextValue);
      if (next === rawHypothesisSearch) return;
      setRawHypothesisSearch(next, 'replaceIn');
    },
    [rawHypothesisSearch, setRawHypothesisSearch],
  );

  const [rawHypothesisUploaded, setRawHypothesisUploaded] = useQueryParam(
    'uploaded',
    StringParam,
  );
  const hypothesisUploaded = isUploadedFilter(rawHypothesisUploaded)
    ? rawHypothesisUploaded
    : DEFAULT_HYPOTHESIS_CREATIVES_VIEW_STATE.uploaded;
  const setHypothesisUploaded = useCallback(
    (nextValue: UploadedFilter) => {
      if (nextValue === hypothesisUploaded) return;
      setRawHypothesisUploaded(nextValue, 'replaceIn');
    },
    [hypothesisUploaded, setRawHypothesisUploaded],
  );

  const [rawHypothesisNames, setRawHypothesisNames] = useQueryParam(
    'hypothesis',
    StringParam,
  );
  const hypothesisNames = useMemo(
    () => decodeFreeTextList(rawHypothesisNames),
    [rawHypothesisNames],
  );
  const setHypothesisNames = useCallback(
    (nextValue: readonly string[]) => {
      const next = encodeFreeTextList(nextValue);
      if (next === rawHypothesisNames) return;
      setRawHypothesisNames(next, 'replaceIn');
    },
    [rawHypothesisNames, setRawHypothesisNames],
  );

  const [rawHypothesisFlow, setRawHypothesisFlow] = useQueryParam('flow', StringParam);
  const hypothesisFlow = useMemo(
    () => decodeFreeTextList(rawHypothesisFlow),
    [rawHypothesisFlow],
  );
  const setHypothesisFlow = useCallback(
    (nextValue: readonly string[]) => {
      const next = encodeFreeTextList(nextValue);
      if (next === rawHypothesisFlow) return;
      setRawHypothesisFlow(next, 'replaceIn');
    },
    [rawHypothesisFlow, setRawHypothesisFlow],
  );

  const [rawHypothesisCustomer, setRawHypothesisCustomer] = useQueryParam(
    'customer',
    StringParam,
  );
  const hypothesisCustomer = decodeFreeTextOrNull(rawHypothesisCustomer);
  const setHypothesisCustomer = useCallback(
    (nextValue: string | null) => {
      const next = encodeFreeTextOrNull(nextValue);
      if (next === rawHypothesisCustomer) return;
      setRawHypothesisCustomer(next, 'replaceIn');
    },
    [rawHypothesisCustomer, setRawHypothesisCustomer],
  );

  const [rawHypothesisTechnology, setRawHypothesisTechnology] = useQueryParam(
    'technology',
    StringParam,
  );
  const hypothesisTechnology = useMemo(
    () => decodeFreeTextList(rawHypothesisTechnology),
    [rawHypothesisTechnology],
  );
  const setHypothesisTechnology = useCallback(
    (nextValue: readonly string[]) => {
      const next = encodeFreeTextList(nextValue);
      if (next === rawHypothesisTechnology) return;
      setRawHypothesisTechnology(next, 'replaceIn');
    },
    [rawHypothesisTechnology, setRawHypothesisTechnology],
  );

  const [rawHypothesisTpcBefore, setRawHypothesisTpcBefore] = useQueryParam(
    'tpc_before',
    StringParam,
  );
  const hypothesisTpcBefore = rawHypothesisTpcBefore || null;
  const setHypothesisTpcBefore = useCallback(
    (nextValue: string | null) => {
      const next = nextValue || undefined;
      if (next === rawHypothesisTpcBefore) return;
      setRawHypothesisTpcBefore(next, 'replaceIn');
    },
    [rawHypothesisTpcBefore, setRawHypothesisTpcBefore],
  );

  const [rawHypothesisStatus, setRawHypothesisStatus] = useQueryParam(
    'status',
    StringParam,
  );
  const hypothesisStatus = isLaunchStatus(rawHypothesisStatus)
    ? rawHypothesisStatus
    : null;
  const setHypothesisStatus = useCallback(
    (nextValue: LaunchStatus | null) => {
      if (nextValue === hypothesisStatus) return;
      setRawHypothesisStatus(nextValue ?? undefined, 'replaceIn');
    },
    [hypothesisStatus, setRawHypothesisStatus],
  );

  const [rawStep, setRawStep] = useQueryParam('step', NumberParam);

  const step = typeof rawStep === 'number' && rawStep >= 0 ? Math.floor(rawStep) : 0;

  const setStep = useCallback<(nextValue: number, updateType?: UrlUpdateType) => void>(
    (nextValue, updateType = 'replaceIn') => {
      const next = nextValue > 0 ? nextValue : undefined;
      if ((next ?? 0) === step) return;
      setRawStep(next, updateType);
    },
    [step, setRawStep],
  );

  return {
    marketingProcess,
    setMarketingProcess,
    app,
    setApp,
    hasConfiguredParams,
    resetLocationParamsForApp,
    resetLocationParamsToStart,
    reloadLocationParamsToStart,
    adType,
    setAdType,
    platform,
    setPlatform,
    adAccounts,
    setAdAccounts,
    adAccount,
    setAdAccount,
    step,
    setStep,
    visualTouchUps,
    setVisualTouchUps,
    callToAction,
    setCallToAction,
    conversionDomain,
    setConversionDomain,
    hotjar,
    setHotjar,
    partnershipAdsEnabled,
    setPartnershipAdsEnabled,
    partnershipIdentityToShow,
    setPartnershipIdentityToShow,
    partnershipIsPartnerIdentityFirst,
    setPartnershipIsPartnerIdentityFirst,
    partnershipCreationMode,
    setPartnershipCreationMode,
    partners,
    setPartners,
    packAdSettings,
    setPackAdSettings,
    partnershipAdSettingsFields,
    setPartnershipAdSettingsFields,
    creativesByPack,
    setCreativesByPack,
    flatCreatives,
    setFlatCreatives,
    creativesFormat,
    selectedCampaigns,
    setSelectedCampaigns,
    filterCampaignsByName,
    setFilterCampaignsByName,
    selectedAdSets,
    setSelectedAdSets,
    filterAdSetsByName,
    setFilterAdSetsByName,
    filterCreativesByName,
    setFilterCreativesByName,
    hypothesisSearch,
    setHypothesisSearch,
    hypothesisUploaded,
    setHypothesisUploaded,
    hypothesisNames,
    setHypothesisNames,
    hypothesisFlow,
    setHypothesisFlow,
    hypothesisCustomer,
    setHypothesisCustomer,
    hypothesisTechnology,
    setHypothesisTechnology,
    hypothesisTpcBefore,
    setHypothesisTpcBefore,
    hypothesisStatus,
    setHypothesisStatus,
  };
};

export default useLocationParams;
